/**
 * "By customer" view: groups the rows of the CURRENT page. Grouping is client
 * side, so a customer whose deployments span several pages appears once per
 * page; the page says so.
 */
import type { DeploymentRequest } from '@/types'

export interface CustomerGroup {
  key: string
  /** Display name of the customer. */
  label: string
  /** 3-digit WorkWise customer number, or null for other products. */
  customerNumber: string | null
  rows: DeploymentRequest[]
  environments: string[]
  /** Sum of vm_count over the WorkWise rows. */
  vmCount: number
  /** Sum of est_monthly_cost over the WorkWise rows. */
  estMonthlyCost: number
  hasWorkwise: boolean
}

/** WorkWise customers group by number (stable across environments), others by name. */
export function customerKey(row: DeploymentRequest): string {
  if (row.workwise) return `ww:${row.workwise.customer_number}`
  return `name:${(row.customer_name || row.tenant_id || String(row.id)).trim().toLowerCase()}`
}

export function groupByCustomer(rows: DeploymentRequest[]): CustomerGroup[] {
  const groups = new Map<string, CustomerGroup>()
  for (const row of rows) {
    const key = customerKey(row)
    let group = groups.get(key)
    if (!group) {
      group = {
        key,
        label: row.customer_name || row.tenant_id || `Deployment #${row.id}`,
        customerNumber: row.workwise?.customer_number ?? null,
        rows: [],
        environments: [],
        vmCount: 0,
        estMonthlyCost: 0,
        hasWorkwise: false,
      }
      groups.set(key, group)
    }
    group.rows.push(row)
    if (!group.environments.includes(row.environment)) group.environments.push(row.environment)
    if (row.workwise) {
      group.hasWorkwise = true
      group.vmCount += row.workwise.vm_count
      const cost = Number(row.workwise.est_monthly_cost)
      if (Number.isFinite(cost)) group.estMonthlyCost += cost
    }
  }
  return [...groups.values()]
}
